import React, { useState, useRef, useEffect } from "react";
import { getDocs, collection, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Password from "./Password";
import Window from "./Window";
import { db, storage } from "../firebase";

const MusicPlayer = () => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const [volume, setVolume] = useState(0.5);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);

    const [isPasswordOpen, setIsPasswordOpen] = useState(false);
    const [isAddTrackOpen, setIsAddTrackOpen] = useState(false);

    const defaultTrack = {
        title: "青春リバイバル",
        trackURL: "/assets/music/track1.mp3",
        imgURL: "/assets/music/cover1.jpg",
    };

    const [tracks, setTracks] = useState([defaultTrack]);

    // 새 트랙 추가 폼
    const [newTitle, setNewTitle] = useState("");
    const [newTrackFile, setNewTrackFile] = useState(null);
    const [newCoverFile, setNewCoverFile] = useState(null);

    // Firestore에서 트랙 불러오기
    const fetchTracks = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "tracks"));
            const trackData = querySnapshot.docs.map((doc) => doc.data());

            if (trackData.length > 0) {
                setTracks(trackData);
                const randomIndex = Math.floor(Math.random() * trackData.length);
                setCurrentTrackIndex(randomIndex);
            } else {
                setTracks([defaultTrack]);
                setCurrentTrackIndex(0);
            }
        } catch (error) {
            console.error("트랙 로드 실패:", error);
            setTracks([defaultTrack]);
            setCurrentTrackIndex(0);
        }
    };


    useEffect(() => {
        fetchTracks();
    }, []);

    const onPasswordSuccess = () => {
        setIsPasswordOpen(false);
        setIsAddTrackOpen(true);
    };

    const toggleVolumeSlider = () => {
        setShowVolumeSlider(!showVolumeSlider);
    };

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (isPlaying) audioRef.current.pause();
        else audioRef.current.play();
        setIsPlaying(!isPlaying);
    };

    const playNext = async () => {
        if (tracks.length === 0) return;
        let nextIndex = currentTrackIndex;

        while (tracks.length > 1 && nextIndex === currentTrackIndex) {
            nextIndex = Math.floor(Math.random() * tracks.length);
        }

        setCurrentTrackIndex(nextIndex);
        setIsPlaying(true);

        // 다음 트랙으로 바뀐 후 재생되도록 약간 딜레이 주기
        setTimeout(() => {
            if (audioRef.current) {
                audioRef.current.play().catch((e) => {
                    console.warn("자동 재생이 차단됨:", e);
                });
            }
        }, 100); // 약간 딜레이 줘야 오디오가 제대로 바뀐 다음 재생돼
    };

    const playPrev = () => {
        if (tracks.length === 0) return;
        const prevIndex = (currentTrackIndex - 1 + tracks.length) % tracks.length;
        setCurrentTrackIndex(prevIndex);
        setIsPlaying(true);
    };

    const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        const percent = (audioRef.current.currentTime / audioRef.current.duration) * 100;
        setProgress(percent);
    };

    const handleProgressClick = (e) => {
        if (!audioRef.current) return;
        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const width = rect.width;
        const newTime = (offsetX / width) * audioRef.current.duration;
        audioRef.current.currentTime = newTime;
    };

    const handleVolumeChange = (e) => {
        const vol = parseFloat(e.target.value);
        setVolume(vol);
        if (audioRef.current) audioRef.current.volume = vol;
    };

    const handleAddTrack = async () => {
        if (!newTitle || !newTrackFile || !newCoverFile) {
            alert("모든 정보를 입력해 주세요!");
            return;
        }

        try {
            const trackRef = ref(storage, `tracks/${Date.now()}_${newTrackFile.name}`);
            const coverRef = ref(storage, `covers/${Date.now()}_${newCoverFile.name}`);

            await uploadBytes(trackRef, newTrackFile);
            await uploadBytes(coverRef, newCoverFile);

            const trackURL = await getDownloadURL(trackRef);
            const imgURL = await getDownloadURL(coverRef);

            await addDoc(collection(db, "tracks"), {
                title: newTitle,
                trackURL,
                imgURL,
            });

            await fetchTracks();

            setNewTitle("");
            setNewTrackFile(null);
            setNewCoverFile(null);
            setIsAddTrackOpen(false);
        } catch (error) {
            console.error("트랙 추가 실패:", error);
            alert("트랙 추가 중 오류가 발생했습니다.");
        }
    };

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume;
        }
    }, [volume]);

    useEffect(() => {
        if (!audioRef.current) return;
        if (isPlaying) audioRef.current.play();
        else audioRef.current.pause();
    }, [isPlaying, currentTrackIndex]);

    const currentTrack = tracks[currentTrackIndex] || defaultTrack;

    return (
        <div className="w-full max-w-md h-full max-h-md text-blue-500 p-2 flex flex-col items-center relative">
            {/* 볼륨 아이콘 */}
            <div className="absolute right-2 flex items-center">
                <button
                    onClick={toggleVolumeSlider}
                    className="cursor-pointer select-none"
                    aria-label="볼륨 조절 토글"
                    style={{ fontSize: '1.25vw' }}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="size-[1em]"
                    >
                        <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 0 0 1.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06ZM18.584 5.106a.75.75 0 0 1 1.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 0 1-1.06-1.06 8.25 8.25 0 0 0 0-11.668.75.75 0 0 1 0-1.06Z" />
                        <path d="M15.932 7.757a.75.75 0 0 1 1.061 0 6 6 0 0 1 0 8.486.75.75 0 0 1-1.06-1.061 4.5 4.5 0 0 0 0-6.364.75.75 0 0 1 0-1.06Z" />
                    </svg>
                </button>

                {showVolumeSlider && (
                    <input
                        id="volume"
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={handleVolumeChange}
                        className="absolute -right-20 top-12 rotate-[-90deg] accent-blue-300"
                    />
                )}
            </div>

            {/* 추가 버튼 */}
            <button
                onClick={() => setIsPasswordOpen(true)}
                className="cursor-pointer select-none absolute right-2 top-10"
                style={{ fontSize: '1.25vw' }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-[1em]">
                    <path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 9a.75.75 0 0 0-1.5 0v2.25H9a.75.75 0 0 0 0 1.5h2.25V15a.75.75 0 0 0 1.5 0v-2.25H15a.75.75 0 0 0 0-1.5h-2.25V9Z" clip-rule="evenodd" />
                </svg>

            </button>

            {/* 비밀번호 팝업 */}
            {isPasswordOpen && (
                <div className="fixed inset-0 flex justify-center items-center z-50">
                    <Window
                        className="shadow-lg"
                        onClose={() => setIsPasswordOpen(false)}
                        content={
                            <Password
                                onClose={() => setIsPasswordOpen(false)}
                                onSuccess={onPasswordSuccess}
                            />
                        }
                    />
                </div>
            )}

            {/* 트랙 추가 팝업 */}
            {isAddTrackOpen && (
                <div className="fixed inset-0 flex justify-center items-center z-50">
                    <Window
                        className="shadow-lg"
                        onClose={() => setIsAddTrackOpen(false)}
                        content={
                            <div className="flex flex-col gap-4 items-center p-6 pt-9 rounded-lg shadow-lg w-[320px] relative">
                                <button
                                    className="absolute top-0 right-3 w-3 h-3"
                                    onClick={() => setIsAddTrackOpen(false)}
                                >
                                </button>

                                <h2 className="text-xl font-bold mb-2">트랙 추가</h2>

                                <input
                                    type="text"
                                    placeholder="제목"
                                    value={newTitle}
                                    onChange={(e) => setNewTitle(e.target.value)}
                                    className="w-full p-2 rounded border border-gray-300"
                                />
                                <input
                                    type="file"
                                    accept="audio/*"
                                    onChange={(e) => setNewTrackFile(e.target.files[0])}
                                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-400 file:text-white hover:file:bg-blue-500 file:cursor-pointer file:content-['오디오_선택하기']"
                                />
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setNewCoverFile(e.target.files[0])}
                                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-400 file:text-white hover:file:bg-blue-500 cursor-pointer"
                                />

                                <button
                                    onClick={handleAddTrack}
                                    className="bg-blue-400 hover:bg-blue-500 text-white py-2 px-6 rounded font-semibold transition mt-2"
                                >
                                    업로드하기
                                </button>
                            </div>
                        } />

                </div>
            )}
            {/* 앨범커버 이미지 */}
            <div className="relative w-[80%] aspect-square mb-2">
                <div className="absolute w-[85%] bottom-[8%] aspect-square left-1/2 -translate-x-1/2 bg-white border border-black shadow-md z-10" />
                <img
                    src={currentTrack.imgURL}
                    alt={currentTrack.title}
                    className="w-full h-full rounded-full object-cover relative border border-gray-300 z-20"
                    style={{ marginBottom: '-20%' }}
                />
                <div className="absolute w-[25%] bottom-[37%] aspect-square rounded-full left-1/2 -translate-x-1/2 bg-white border border-gray-300 z-30" />
            </div>

            {/* 타이틀 */}
            <p className="font-light mb-[1%]" style={{ fontSize: '0.8vw' }}>
                {currentTrack.title}
            </p>

            {/* 재생 버튼 */}
            <div className="flex items-center justify-center gap-6">
                <button onClick={playPrev} style={{ fontSize: '1.5vw' }}>⏮</button>
                <button onClick={togglePlay} style={{ fontSize: '1.5vw' }}>
                    {isPlaying ? '⏸' : '▶'}
                </button>
                <button onClick={playNext} style={{ fontSize: '1.5vw' }}>⏭</button>
            </div>

            {/* 진행바 */}
            <div
                className="mt-[2%] w-full h-2 bg-gray-600 rounded cursor-pointer"
                onClick={handleProgressClick}
            >
                <div
                    className="h-2 bg-blue-400 rounded"
                    style={{ width: `${progress}%` }}
                />
            </div>

            {/* 오디오 태그 (실제 음악 재생) */}
            <audio
                ref={audioRef}
                src={currentTrack.trackURL}
                onTimeUpdate={handleTimeUpdate}
                onEnded={playNext}
            />
        </div>
    );
};

export default MusicPlayer;
